<template>
  <div>
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{
            $t(
              isNew
                ? "CRUD.newInfo"
                : savable
                ? "CRUD.editInfo"
                : "CRUD.viewInfo",
              [$t("AccountUser.ObjectName")]
            )
          }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="
                  $emit('mode', null);
                  $emit('input', null);
                "
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("AccountUser.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.name") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="accountUser.name"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'name')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.number") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.number')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="accountUser.job_number"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'job_number')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.job_number }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("AccountUser.Fields.organization") + $t("Global.colon")
                  }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.organization')"
                    rules="required"
                  >
                    <SearcherOrganization
                      v-model="accountUser.organization"
                      no-label
                      unclearable
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    accountUser && accountUser.organization
                      ? accountUser.organization.name
                      : ""
                  }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.position") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field single-line v-model="accountUser.position">
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.position }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.role") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.role')"
                    rules="required"
                  >
                    <SearcherRole
                      v-model="accountUser.role"
                      no-label
                      unclearable
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    accountUser && accountUser.role
                      ? accountUser.role.role_name
                      : ""
                  }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.sex") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="accountUser.sex">
                    <v-radio
                      v-for="s in sex"
                      :key="s.value"
                      :label="`${s.text}`"
                      :value="s.value"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.sex | sexFlgsText }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.mobile") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.mobile')"
                    rules="mobile"
                  >
                    <v-text-field
                      v-model="accountUser.mobile"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'mobile')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.mobile }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.email") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.email')"
                    rules="email"
                  >
                    <v-text-field
                      v-model="accountUser.email"
                      single-line
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'email')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.email }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.login_name") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.login_name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="accountUser.account_user_id"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'account_user_id')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ accountUser.account_user_id }}
                </v-col>
              </v-row>
              <v-row align="center" v-if="savable && isNew">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.password") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.password')"
                    rules="required|min:6"
                  >
                    <v-text-field
                      single-line
                      v-model="accountUser.password"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'password')
                          .concat(errors)
                      "
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row align="center" v-else-if="savable && !isNew">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("AccountUser.Fields.password") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('AccountUser.Fields.password')"
                    rules="min:6"
                  >
                    <v-text-field
                      single-line
                      v-model="accountUser.password"
                      :error-messages="errors"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </ValidationObserver>
          </v-container>
        </v-card-text>
      </v-card>
      <v-sheet class="page-opts">
        <v-card>
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  outlined
                  color="secondary"
                  class="elevation-1"
                  block
                  @click="
                    $emit('input', null);
                    $emit('mode', null);
                  "
                >
                  <v-icon small class="pe-1">fa-times</v-icon>
                  {{ $t("CRUD.backToList", [$t("AccountUser.ObjectName")]) }}
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                v-if="
                  isView && $permission.permissionAllow('AccountUsers', 'edit')
                "
              >
                <v-btn
                  color="accent"
                  block
                  :disabled="loading && !accountUser.id"
                  @click="$emit('mode', 'edit')"
                >
                  <v-icon small class="pe-1">fa-pencil-alt</v-icon>
                  {{ $t("CRUD.editInfo", [$t("AccountUser.ObjectName")]) }}
                </v-btn>
              </v-col>
              <v-col
                cols="12"
                v-if="
                  savable &&
                  $permission.permissionAllow(
                    'AccountUsers',
                    isNew ? 'add' : 'edit'
                  )
                "
              >
                <v-btn
                  color="success"
                  block
                  :disabled="loading && !accountUser.id"
                  @click="submit()"
                >
                  <v-icon small class="pe-1">fa-save</v-icon>
                  {{ $t("CRUD.saveInfo", [$t("AccountUser.ObjectName")]) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
      </v-sheet>
    </v-sheet>
  </div>
</template>
<script>
import { mapState } from "vuex";
import Required from "@/components/plugins/Required";
import Consts from "@/consts.js";
import SearcherOrganization from "./SearcherOrganization";
import SearcherRole from "./SearcherRole";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import AccountUsers from "@/api/account_users";

export default {
  name: "AccountUserDetail",

  props: ["value", "mode"],

  components: {
    Required,
    ValidationObserver,
    ValidationProvider,
    SearcherOrganization,
    SearcherRole,
  },

  computed: {
    isView() {
      return !this.mode || this.mode == "view";
    },
    savable() {
      return this.mode == "edit" || this.mode == "new";
    },
    isNew() {
      return this.mode == "new";
    },
    sex() {
      return Consts.account_user.sex;
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    accountUser: {},
    serverErrors: [],
  }),

  methods: {
    submit() {
      this.$store.dispatch("fullscreenLoading");
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            var accountUser = JSON.parse(JSON.stringify(this.accountUser));
            if (accountUser.organization && accountUser.organization.code) {
              accountUser.organization_code =
                this.accountUser.organization.code;
            }
            if (accountUser.role) {
              accountUser.role_id = this.accountUser.role.id;
            }
            delete accountUser.organization;
            delete accountUser.role;

            AccountUsers.save(
              this.accountUser.id,
              Object.assign({}, accountUser, { type: 2 })
            )
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", accountUser);
                    this.$emit("mode", null);
                    this.$emit("input", null);
                    this.$refs.observer.reset();
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.serverErrors = []; //重置远端错误提示
        if (this.value && this.value.id) {
          //员工详情
          AccountUsers.fetchOne(this.value.id).then(
            function (response) {
              if (response.data.status.isOK()) {
                this.accountUser = response.data.account_user;

                if (
                  this.accountUser.role_id == null ||
                  this.accountUser.role_id == "null"
                ) {
                  this.accountUser.role_id = 0;
                }
                if (!this.accountUser.role)
                  this.accountUser.role = { id: this.accountUser.role_id };
              }
            }.bind(this)
          );
        } else {
          // 新建员工，初始化默认值
          this.accountUser = {
            sex: 1,
            role_id: 0,
            organization_code: "",
          };
        }
        this.$scrollTop(); //返回顶部
      },
    },
  },
};
</script>